/**
 * What the portal can attach help to. A help key is a lower-case string the server maps to an
 * article (`GET /help/context/{key}`): the UI asks for a key and shows whatever article answers, if any.
 */
import { ApiError } from "./http";

/**
 * Keys for the complex fields and statuses of each area. Add a key here when a screen gets a help
 * hint; the article that answers it is edited by staff, so a key with no article just shows nothing.
 */
export const HELP_KEYS = {
  domains: {
    transferStatus: "domains.transfer-status",
    transferLock: "domains.transfer-lock",
    transferOut: "domains.transfer-out",
    nameservers: "domains.nameservers",
    dnssec: "domains.dnssec",
    whoisPrivacy: "domains.whois-privacy",
    expiryAndRecovery: "domains.expiry-and-recovery",
    connection: "domains.connection",
  },
  billing: {
    paymentMethods: "billing.payment-methods",
    directDebit: "billing.direct-debit",
    invoicesAndVat: "billing.invoices-and-vat",
    planLimits: "billing.plan-limits",
  },
  team: {
    roles: "team.roles",
    accessScopes: "team.access-scopes",
  },
  websites: {
    deployments: "websites.deployments",
    customDomains: "websites.custom-domains",
  },
  hosting: {
    externalSites: "hosting.external-sites",
    storage: "hosting.storage",
  },
} as const;

type Values<T> = T[keyof T];

/** A key from the registry, or an `error:<reason>` key from `helpKeyForError`. */
export type HelpKey = Values<{ [G in keyof typeof HELP_KEYS]: Values<(typeof HELP_KEYS)[G]> }>;

/**
 * The help key for a failed request: `error:<reason>` from the problem document's stable `reason`
 * (lower-cased, e.g. `error:payment_declined`), or `error:generic` for anything else.
 */
export function helpKeyForError(error: unknown): string {
  const reason = error instanceof ApiError ? error.reason.trim().toLowerCase() : "";
  return reason ? `error:${reason}` : "error:generic";
}
